import { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

import { Style, StyleStatus } from "../../../../core/models/entities/Style";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { WarnButton } from "../../../components/Buttons/SecondaryButton";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { StyleListItemWrapper } from "../subcomponents/style-list-item.wrapper";
import { styleColumnConfig } from "../const/styleColumnConfig";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { FilterRecipe } from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { useMultiSelectStateMachine } from "../../../shared/hooks/useMultiSelectStateMachine";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { BulkPpcmEditDialog } from "../components/BulkPpcmEditDialog";
import { withToasts } from "../../../components/Toast/Toast";

export function useFunctionalListStyleViewModel(
  manufacturers: Manufacturer[],
  query: PageQuery = {} as PageQuery,
  queryChanger: QueryChanger,
  lastPage: number,
  isPageLoading: boolean,
  styles?: Style[]
): TableData<Style> {
  const navigate = useNavigate();
  const { role } = useProfileContext();
  const { styleRepository } = useRepositoriesContext();
  const [isBulkPpcmDialogOpen, setIsBulkPpcmDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    selectedData,
    onRowChecked,
    onMultiSelectRow,
    isMultiSelect,
    toggleMultiSelect,
    resetSelection,
  } = useMultiSelectStateMachine<Style>({
    onSelect: handleStyle,
    enabled: true,
  });

  function handleStyle(style: Style) {
    // Handle single style selection
  }

  function handleUploadCatalog() {
    navigate("/style/catalog");
  }

  function handleBulkPpcmUpdate() {
    setIsBulkPpcmDialogOpen(true);
  }

  async function handleBulkDeleteStyles() {
    if (isBulkDeleting) {
      return;
    }

    const selectedStyles = Object.values(selectedData);
    if (!selectedStyles.length) {
      return;
    }

    async function transaction() {
      await styleRepository.deleteStyles(
        selectedStyles.map((style) => style.id)
      );
    }

    try {
      await withToasts(
        transaction,
        "Deleting selected styles...",
        "Selected styles deleted",
        "Failed to delete selected styles",
        setIsBulkDeleting
      );
      resetSelection();
    } catch (error) {
      console.error("Failed to delete selected styles", error);
    }
  }

  function handleCloseBulkPpcmDialog() {
    setIsBulkPpcmDialogOpen(false);
  }

  const filters: FilterRecipe<any>[] = [
    {
      options: manufacturers,
      key: "manufacturer.id",
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: "Manufacturers",
    } as FilterRecipe<Manufacturer>,
  ];

  const center = <ViewPendingCount />;

  const selectedCount = Object.values(selectedData)?.length || 0;
  const right = (
    <>
      <PrimaryButton key={"new-order"} onClick={handleUploadCatalog}>
        Upload Catalog
      </PrimaryButton>

      <PrimaryButton
        key={"bulk-ppcm"}
        onClick={handleBulkPpcmUpdate}
        disabled={selectedCount === 0}
      >
        Update PPCM ({selectedCount})
      </PrimaryButton>

      <WarnButton
        key={"bulk-delete-styles"}
        onClick={handleBulkDeleteStyles}
        disabled={selectedCount === 0 || isBulkDeleting}
      >
        {isBulkDeleting ? "Deleting..." : `Delete Styles (${selectedCount})`}
      </WarnButton>
    </>
  );

  return {
    onNewPage: queryChanger.handleNewPage,
    onNewSort: queryChanger.handleSortByField,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    onFilterChange: queryChanger.handleFilterChange,
    center: center,
    right: right,
    isLoading: isPageLoading,
    query: query,
    filters: filters,
    multiSelect: true,
    selectedData: selectedData,
    onRowSelected: onRowChecked,
    lastPage: lastPage,
    data: styles,
    columns: styleColumnConfig,
    renderRow: renderRow,
    getKey: getKey,
    onRowClick: onMultiSelectRow,
    tableTitle: "Styles",
    getRowColor: styleStatusBasedBg,
    component: (
      <BulkPpcmEditDialog
        open={isBulkPpcmDialogOpen}
        onClose={handleCloseBulkPpcmDialog}
        selectedStyles={Object.values(selectedData)}
        onUpdateComplete={() => {
          // Refresh the styles list after update
          // This would need to be implemented based on your data fetching strategy
        }}
      />
    ),
  };
}

function styleStatusBasedBg(style: Style) {
  switch (style.status) {
    case StyleStatus.NotAnnotated:
      return "bg-row-warn";
    case StyleStatus.Annotated:
      return "bg-row-standard";
  }
}

function getKey(style: Style) {
  return style.id;
}

function renderRow(columns: ColumnSettings, data: Style) {
  return <StyleListItemWrapper style={data} columns={columns} />;
}

function ViewPendingCount(): JSX.Element {
  const [viewsPendingCount, setViewsPendingCount] = useState<number>(0);
  const { styleRepository } = useRepositoriesContext();

  function getViewsPendingCount() {
    styleRepository.getCreateMaskQueueCount().then((count) => {
      setViewsPendingCount(count);
    });
  }

  useEffect(() => {
    let interval: NodeJS.Timer | undefined = undefined;

    interval = setInterval(() => {
      getViewsPendingCount();
    }, 20000);
    getViewsPendingCount();

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {viewsPendingCount !== undefined && (
        <div
          className={`w-[165px] flex justify-center items-center mr-2 rounded  border px-2 py-1 space-x-2 text-xs ${
            viewsPendingCount > 0
              ? "bg-status-pending-2 border-status-pending-1 text-status-pending-1"
              : "!border-status-success bg-status-success-bg text-status-success "
          }`}
        >
          {viewsPendingCount > 0 ? (
            <>
              <span className="whitespace-nowrap">
                {viewsPendingCount} Images processing
              </span>
              <div className="text-status-pending-1">
                <CircularProgress color={"inherit"} size={15} />
              </div>
            </>
          ) : (
            <>
              <span className="whitespace-nowrap text-status-success">
                All Processed
              </span>
              <div className="text-status-success">
                <FontAwesomeIcon size={"xl"} icon={faCheck} />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
